import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Clock,
  Languages,
  FileText,
  Save,
  Upload,
  Loader2,
  Settings2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'UTC', label: 'UTC' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
];

const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
];

const TIME_FORMATS = [
  { value: '12h', label: '12-hour (AM/PM)' },
  { value: '24h', label: '24-hour' },
];

/**
 * Platform Profile Settings Page
 * 
 * Core platform settings including:
 * - Business information
 * - Contact details
 * - Regional settings (timezone, language, date/time formats)
 */
const PlatformProfileSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    platform_name: 'FundElite',
    legal_name: 'FundElite LLC',
    contact_email: '',
    contact_phone: '',
    website: '',
    support_email: '',
    address: '',
    city: '',
    state: '',
    country: 'US',
    postal_code: '',
    timezone: 'America/New_York',
    language: 'en',
    date_format: 'MM/DD/YYYY',
    time_format: '12h',
    notes: '',
  });
  
  // Load platform settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/platform/settings/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data) {
          setFormData(prev => ({ ...prev, ...res.data }));
        }
      } catch (err) {
        console.error('Failed to load platform settings:', err);
        // Use defaults if not found
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, [token]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/platform/settings/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Platform profile updated successfully');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  // Get current time in selected timezone
  const getCurrentTime = () => {
    try {
      return new Date().toLocaleString('en-US', { 
        timeZone: formData.timezone,
        dateStyle: 'full',
        timeStyle: 'long'
      });
    } catch {
      return new Date().toLocaleString();
    }
  };
  
  return (
    <div className="p-6 space-y-6" data-testid="platform-profile-settings">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <Building2 className="text-blue-600" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Platform Profile</h1>
            <p className="text-slate-500">
              Configure your platform's identity and regional settings
            </p>
          </div>
        </div>
        <Badge variant="secondary" className="text-sm">
          <Clock className="w-3 h-3 mr-1" />
          {getCurrentTime()}
        </Badge>
      </div>
      
      {/* Form Content */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Business Identity Section */}
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <Building2 size={18} className="text-amber-500" />
            Business Identity
          </h2>
          
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="platform_name">Platform Name</Label>
              <Input
                id="platform_name"
                value={formData.platform_name}
                onChange={(e) => handleChange('platform_name', e.target.value)}
                placeholder="My Platform"
                className="bg-white"
              />
              <p className="text-xs text-slate-500">Displayed in the header and emails</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="legal_name">Legal Business Name</Label>
              <Input
                id="legal_name"
                value={formData.legal_name}
                onChange={(e) => handleChange('legal_name', e.target.value)}
                placeholder="My Platform LLC"
                className="bg-white"
              />
              <p className="text-xs text-slate-500">Used in legal documents and invoices</p>
            </div>
          </div>
          
          {/* Platform Logo */}
          <div className="mt-6 space-y-2">
            <Label>Platform Logo</Label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                <Building2 className="text-slate-400" size={40} />
              </div>
              <div className="space-y-2">
                <Button variant="outline" size="sm">
                  <Upload size={14} className="mr-2" />
                  Upload Logo
                </Button>
                <p className="text-xs text-slate-500">
                  PNG, JPG up to 2MB. Recommended 200x200px
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Contact Information Section */}
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <Mail size={18} className="text-blue-500" />
            Contact Information
          </h2>
          
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="contact_email">
                <Mail size={14} className="inline mr-1" />
                Primary Email
              </Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                placeholder="admin@platform.com"
                className="bg-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="support_email">
                <Mail size={14} className="inline mr-1" />
                Support Email
              </Label>
              <Input
                id="support_email"
                type="email"
                value={formData.support_email}
                onChange={(e) => handleChange('support_email', e.target.value)}
                placeholder="support@platform.com"
                className="bg-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact_phone">
                <Phone size={14} className="inline mr-1" />
                Contact Phone
              </Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="bg-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="website">
                <Globe size={14} className="inline mr-1" />
                Website
              </Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://www.myplatform.com"
                className="bg-white"
              />
            </div>
          </div>
        </div>
        
        {/* Physical Address Section */}
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <MapPin size={18} className="text-green-500" />
            Physical Address
          </h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address">Street Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main Street, Suite 100"
                className="bg-white"
              />
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="New York"
                  className="bg-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="NY"
                  className="bg-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  placeholder="10001"
                  className="bg-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => handleChange('country', value)}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="US">United States</SelectItem>
                    <SelectItem value="CA">Canada</SelectItem>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>
        
        {/* Regional Settings Section */}
        <div className="p-6 border-b border-slate-200 bg-blue-50/50">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-2">
            <Clock size={18} className="text-purple-500" />
            Regional Settings
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            These settings affect how dates and times are displayed throughout the admin portal
          </p>
          
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="timezone" className="flex items-center gap-2">
                <Clock size={14} />
                Time Zone
                <Badge className="bg-blue-100 text-blue-700 text-xs">Primary</Badge>
              </Label>
              <Select
                value={formData.timezone}
                onValueChange={(value) => handleChange('timezone', value)}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {TIMEZONES.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                All times in the admin portal will use this timezone
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="language">
                <Languages size={14} className="inline mr-1" />
                Language
              </Label>
              <Select
                value={formData.language}
                onValueChange={(value) => handleChange('language', value)}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="date_format">
                <Settings2 size={14} className="inline mr-1" />
                Date Format
              </Label>
              <Select
                value={formData.date_format}
                onValueChange={(value) => handleChange('date_format', value)}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {DATE_FORMATS.map(fmt => (
                    <SelectItem key={fmt.value} value={fmt.value}>
                      {fmt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="time_format">
                <Clock size={14} className="inline mr-1" />
                Time Format
              </Label>
              <Select
                value={formData.time_format}
                onValueChange={(value) => handleChange('time_format', value)}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {TIME_FORMATS.map(fmt => (
                    <SelectItem key={fmt.value} value={fmt.value}>
                      {fmt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Current Time Display */}
          <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200">
            <p className="text-sm text-slate-600">
              <span className="font-medium">Current time in {formData.timezone}:</span>
            </p>
            <p className="text-lg font-semibold text-slate-900 mt-1">
              {getCurrentTime()}
            </p>
          </div>
        </div>
        
        {/* Notes Section */}
        <div className="p-6">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4">
            <FileText size={18} className="text-slate-500" />
            Notes
          </h2>
          
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Internal notes about platform configuration..."
            rows={4}
            className="bg-white"
          />
        </div>
      </div>
      
      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} className="mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PlatformProfileSettings;
